<template>
	<Teleport to="body">
		<transition name="tutorial-tour-fade">
			<div
				v-if="active && step"
				class="tutorial-tour-root fixed inset-0"
				:class="{ 'pointer-events-none': isClickThrough }"
				:style="{ zIndex: 2147483647 }"
			>
				<!--
					Spotlight: a fixed rect whose large box-shadow dims everything around the target. Left out until it has
					been measured, or its shadow would dim the whole page while a step waits for its target.
				-->
				<div
					v-if="isSpotlightPlaced"
					class="tutorial-tour-spotlight pointer-events-none fixed"
					:class="isBelow ? 'tutorial-tour-spotlight--below rounded-lg' : 'rounded-md'"
					:style="spotlightStyle"
				/>

				<!--
					An interactive step lets clicks through the cut-out: the root ignores the pointer and these four
					blockers cover the scrim around the cut-out instead. That waits until the step is revealed, so the
					root keeps blocking the page while the step waits for its target, and no stale cut-out of the
					previous step takes clicks. The blockers are drawn from the same rect as the spotlight.
				-->
				<template v-if="isClickThrough">
					<div
						v-for="(blockerStyle, index) in blockerStyles"
						:key="index"
						class="pointer-events-auto fixed"
						data-tutorial-blocker
						:style="blockerStyle"
					/>
				</template>

				<!--
					Tooltip card: fixed, viewport-clamped so it can never land off-screen. Hidden, not only transparent, until
					it is placed, so its buttons cannot be clicked or focused where nobody sees them.
				-->
				<div
					ref="tooltipEl"
					class="tutorial-tour-tooltip fixed flex flex-col rounded-lg border-won-green bg-white"
					:class="[
						isBelow
							? 'gap-2 border-2 p-2.5 drop-shadow-[0_4px_2px_rgba(0,0,0,0.25)]'
							: 'gap-3 border p-3 shadow-lg',
						{ 'pointer-events-auto': isInteractive },
					]"
					:style="[tooltipStyle, { opacity: ready ? 1 : 0, visibility: ready ? 'visible' : 'hidden' }]"
					role="dialog"
					aria-modal="true"
					:aria-labelledby="titleId"
					:aria-describedby="bodyId"
				>
					<!--
						A tour can bring its own card content. It must render the title under `titleId` and the body
						under `bodyId`, which name and describe the dialog, and mark its main button with
						`data-tutorial-primary`, which takes focus when a step opens.
					-->
					<slot v-bind="cardProps">
						<div class="flex w-full items-center justify-end">
							<button
								type="button"
								class="text-[#9ca3af] hover:text-won-black"
								aria-label="Close tutorial"
								@click="$emit('close')"
							>
								<XMarkIcon class="size-4" />
							</button>
						</div>

						<div>
							<div
								:id="titleId"
								class="font-Inter text-[16px] leading-5 font-medium text-[#111827]"
							>
								{{ step.title }}
							</div>

							<div
								:id="bodyId"
								class="mt-0.5 font-Inter text-[14px] leading-5 text-[#6b7280]"
							>
								{{ step.body }}
							</div>
						</div>

						<div
							v-if="stepCount > 1"
							class="flex items-center justify-center gap-1"
						>
							<span
								v-for="dot in stepCount"
								:key="dot"
								class="h-1 rounded-sm bg-[#9ca3af] transition-all"
								:class="dot - 1 === stepIndex ? 'w-3' : 'w-1 rounded-full'"
							/>
						</div>

						<div class="flex w-full items-center justify-between">
							<Button
								variant="secondary"
								size="medium"
								:class="isFirstStep ? 'invisible' : ''"
								:disabled="isFirstStep"
								@click="$emit('previous')"
							>
								Previous
							</Button>

							<span class="font-Inter text-[14px] text-[#6b7280]">
								{{ stepIndex + 1 }} of {{ stepCount }}
							</span>

							<Button
								variant="primary"
								size="medium"
								data-tutorial-primary
								@click="$emit('next')"
							>
								{{ isLastStep ? 'Finish' : 'Next' }}
							</Button>
						</div>
					</slot>
				</div>
			</div>
		</transition>
	</Teleport>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, shallowRef, watch } from 'vue';
import { useEventListener, useMutationObserver, useResizeObserver } from '@vueuse/core';
import { XMarkIcon } from '@heroicons/vue/24/outline';

import { isMobileViewport } from '../../composables/isMobile.js';
import Button from '../library/button.vue';

// Reusable coach-mark stepper: parameterized entirely by `step`/`stepCount`/`stepIndex` so the
// same component renders EN-805's 6-step new-user tour and can be reused as-is for a shorter
// step array (e.g. EN-806's "what's new" tour for existing users). Anchoring/measuring mirrors
// components/auto-apply/auto-apply-review-tour.vue (position:fixed + getBoundingClientRect,
// no intro.js) — that pattern was adopted in this epic specifically because intro.js couldn't
// reliably track targets inside scrollable/collapsible panels; this tour has the same needs
// (bullet generator panel, resume score sidebar), so it follows the same approach.
const props = defineProps({
	active: { type: Boolean, default: false },
	// { target: string (CSS selector), title, body, position?, enterDelay?: number in ms —
	// wait this long before measuring, for steps whose onEnter (see use-tutorial-tour.js)
	// opens a panel with a CSS transition. onEnter/onLeave themselves are read by the
	// composable, not this component.
	// Three opt-in fields (none of them changes a step that leaves them out):
	// interactive?: true — the user is meant to click the target itself, so the cut-out lets clicks
	// through to the page, everything around it stays blocked, and the card stays clickable. The
	// click is reported as `target-click` and left to the page's own handler.
	// placement?: 'left' — with layout 'below', the card sits left of the cut-out, top-aligned with
	// it (a panel too tall for a card under it), instead of under it.
	// scrollBlock?: 'start' | 'center' | 'none' — which edge of the viewport the target is scrolled to; 'start'
	// for a target taller than the viewport, 'center' (the default) otherwise. 'none' leaves the page where it
	// is, for a target already in place whose card needs the room above it. }
	step: { type: Object, default: null },
	stepCount: { type: Number, default: 0 },
	stepIndex: { type: Number, default: 0 },
	// 'auto' places the card on whichever side of the target fits, starting from the step's
	// `position` hint, centred on the target. 'below' is the AutoApply Queue coach mark (EN-1250): a
	// 283 px card under the cut-out, or above it when there is no room, its left edge on the cut-out's,
	// over a darker scrim. A step's `placement: 'left'` moves that card beside the cut-out; 'auto'
	// ignores `placement`.
	layout: {
		type: String,
		default: 'auto',
		validator: (value) => ['auto', 'below'].includes(value),
	},
	// How long a step waits for its target to mount before the tour closes: a step on a page that is
	// still loading (EN-1250's queue row). 0 closes at once.
	targetTimeout: { type: Number, default: 0 },
});

// `escape` is the caller's to act on: a tour that should close on Escape listens for it. `target-click`
// reports a click on the target of an `interactive` step. `shown` reports the index of a step whose card has just
// become visible. It repeats whenever the step is entered again, such as when `active` goes off and on.
const emit = defineEmits(['next', 'previous', 'close', 'escape', 'target-click', 'shown']);

// Padding around the spotlight, gap between spotlight and card, and the minimum margin the card
// keeps from the viewport edge. CARD_W matches the rendered card width below.
const PAD = 8;
const GAP = 12;
const MARGIN = 12;
const CARD_W = 320;
const CARD_H_FALLBACK = 200;

// The 'below' layout's gap, viewport margin and card width (Figma 02b–03d). A phone keeps the card to its
// pages' 12 px gutter instead (Figma "Mobile 02b–03d", EN-1256).
const BELOW_GAP = 8;
const BELOW_MARGIN = 16;
const BELOW_MARGIN_MOBILE = 12;
const BELOW_CARD_W = 283;

const TARGET_POLL_MS = 100;

const titleId = 'tutorial-tour-title';
const bodyId = 'tutorial-tour-body';

const tooltipEl = ref(null);
const spotlightStyle = ref({});
// Top, bottom, left and right of the cut-out, for an interactive step.
const blockerStyles = ref([]);
// The step's target, once found: what `target-click` listens to.
const targetEl = shallowRef(null);
const tooltipStyle = ref({});
// Hide the card until it has been measured + positioned, so it never flashes at a stale spot.
const ready = ref(false);

const isBelow = computed(() => props.layout === 'below');
const isInteractive = computed(() => props.step?.interactive === true);
// `ready` is set once the step's target is measured, so only then is the cut-out real.
const isClickThrough = computed(() => isInteractive.value && ready.value);
const isSpotlightPlaced = computed(() => spotlightStyle.value.top !== undefined);
const isFirstStep = computed(() => props.stepIndex <= 0);
const isLastStep = computed(() => props.stepCount > 0 && props.stepIndex >= props.stepCount - 1);

const cardProps = computed(() => ({
	step: props.step,
	stepIndex: props.stepIndex,
	stepCount: props.stepCount,
	isFirstStep: isFirstStep.value,
	isLastStep: isLastStep.value,
	titleId,
	bodyId,
}));

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

// Raw (pre-clamp) top/left for a given placement. `s` is the spotlight rect { x, y, w, h }; `c`
// bundles the card + viewport dims { tw, th, vw, vh }.
const cardPosition = (placement, s, c) => {
	switch (placement) {
		case 'left':
			return { left: s.x - GAP - c.tw, top: s.y + s.h / 2 - c.th / 2 };
		case 'right':
			return { left: s.x + s.w + GAP, top: s.y + s.h / 2 - c.th / 2 };
		case 'top':
			return { top: s.y - GAP - c.th, left: s.x + s.w / 2 - c.tw / 2 };
		case 'bottom':
			return { top: s.y + s.h + GAP, left: s.x + s.w / 2 - c.tw / 2 };
		default:
			return { top: c.vh / 2 - c.th / 2, left: c.vw / 2 - c.tw / 2 };
	}
};

const placeAuto = (s, c) => {
	// Seed the preference order with the step's hint, then fall through to whatever fits.
	const hint = props.step?.position;
	const order = hint === 'top' || hint === 'bottom'
		? ['top', 'bottom', 'left', 'right']
		: ['left', 'right', 'top', 'bottom'];

	const fits = {
		left: s.x >= c.tw + GAP + MARGIN,
		right: c.vw - (s.x + s.w) >= c.tw + GAP + MARGIN,
		top: s.y >= c.th + GAP + MARGIN,
		bottom: c.vh - (s.y + s.h) >= c.th + GAP + MARGIN,
	};

	const placement = order.find((side) => fits[side]) || hint || 'centered';

	return cardPosition(placement, s, c);
};

const placeBelow = (s, c) => {
	if (props.step?.placement === 'left') {
		return { top: s.y + BELOW_MARGIN, left: s.x - BELOW_GAP - c.tw };
	}

	const fitsBelow = c.vh - (s.y + s.h) >= c.th + BELOW_GAP + c.margin;
	const fitsAbove = s.y >= c.th + BELOW_GAP + c.margin;

	return {
		top: fitsBelow || ! fitsAbove ? s.y + s.h + BELOW_GAP : s.y - BELOW_GAP - c.th,
		left: s.x,
	};
};

const resolveTarget = () => {
	const { target } = props.step ?? {};
	return target ? document.querySelector(target) : null;
};

const measure = () => {
	if (! props.active) {
		return;
	}

	const el = resolveTarget();

	// A revealed step's target can unmount mid-step (e.g. the row it points at changed): the step closes as if
	// the target had never mounted, rather than keep a stale cut-out, which an interactive step lets clicks through.
	if (! el) {
		if (ready.value) {
			ready.value = false;
			emit('close');
		}

		return;
	}

	targetEl.value = el;

	const rect = el.getBoundingClientRect();
	const spotlight = {
		x: rect.left - PAD,
		y: rect.top - PAD,
		w: rect.width + PAD * 2,
		h: rect.height + PAD * 2,
	};

	spotlightStyle.value = {
		top: `${spotlight.y}px`,
		left: `${spotlight.x}px`,
		width: `${spotlight.w}px`,
		height: `${spotlight.h}px`,
	};

	// The four strips around the cut-out, as wide as the viewport above and below it and as tall as
	// the cut-out beside it, so no two overlap.
	const cutTop = Math.max(0, spotlight.y);
	const cutBottom = Math.max(0, spotlight.y + spotlight.h);
	const cutLeft = Math.max(0, spotlight.x);
	const cutRight = Math.max(0, spotlight.x + spotlight.w);

	blockerStyles.value = [
		{ top: 0, left: 0, right: 0, height: `${cutTop}px` },
		{ top: `${cutBottom}px`, left: 0, right: 0, bottom: 0 },
		{ top: `${cutTop}px`, left: 0, width: `${cutLeft}px`, height: `${cutBottom - cutTop}px` },
		{ top: `${cutTop}px`, left: `${cutRight}px`, right: 0, height: `${cutBottom - cutTop}px` },
	];

	const cardWidth = isBelow.value ? BELOW_CARD_W : CARD_W;
	const belowMargin = isMobileViewport() ? BELOW_MARGIN_MOBILE : BELOW_MARGIN;
	const margin = isBelow.value ? belowMargin : MARGIN;
	const card = {
		vw: window.innerWidth,
		vh: window.innerHeight,
		tw: tooltipEl.value?.offsetWidth || cardWidth,
		th: tooltipEl.value?.offsetHeight || CARD_H_FALLBACK,
		margin,
	};

	const { top, left } = isBelow.value ? placeBelow(spotlight, card) : placeAuto(spotlight, card);

	// The unconditional clamp is the guarantee the card is always fully on-screen.
	tooltipStyle.value = {
		top: `${clamp(top, margin, Math.max(margin, card.vh - card.th - margin))}px`,
		left: `${clamp(left, margin, Math.max(margin, card.vw - card.tw - margin))}px`,
		width: `${cardWidth}px`,
	};
};

// rAF-throttle the live recompute so a burst of scroll/resize events collapses to one measure.
let frame = null;
const recompute = () => {
	if (frame !== null) {
		return;
	}

	frame = requestAnimationFrame(() => {
		frame = null;
		measure();
	});
};

const focusPrimary = () => {
	tooltipEl.value?.querySelector('[data-tutorial-primary]')?.focus();
};

// Bumped by every step entered and by the tour going away, so an entry still waiting for its
// target or its delay knows it has been overtaken.
let entry = 0;

const sleep = (ms) => new Promise((resolve) => {
	setTimeout(resolve, ms);
});

const waitForTarget = async (current) => {
	let el = resolveTarget();

	for (let waited = 0; ! el && waited < props.targetTimeout; waited += TARGET_POLL_MS) {
		await sleep(TARGET_POLL_MS);

		if (current !== entry) {
			return null;
		}

		el = resolveTarget();
	}

	return el;
};

// Entering a step: wait for the target to mount (`targetTimeout`), optionally wait out a panel-open
// CSS transition (`step.enterDelay`, e.g. a step that toggles open a sliding panel via its onEnter
// hook), scroll the target into view, then measure across two frames. The first frame positions the
// now-rendered card; the second re-measures with the card's real height before revealing it.
const enterStep = async () => {
	entry += 1;
	const current = entry;

	ready.value = false;
	targetEl.value = null;

	const el = await waitForTarget(current);

	if (current !== entry) {
		return;
	}

	if (! el || ! document.contains(el)) {
		emit('close');
		return;
	}

	if (props.step?.enterDelay) {
		await sleep(props.step.enterDelay);

		if (current !== entry) {
			return;
		}
	}

	if (props.step?.scrollBlock !== 'none') {
		el.scrollIntoView({ block: props.step?.scrollBlock ?? 'center', inline: 'center' });
	}

	requestAnimationFrame(() => {
		measure();

		requestAnimationFrame(() => {
			if (current !== entry) {
				return;
			}

			measure();
			ready.value = true;
			emit('shown', props.stepIndex);
			// Once the card is visible: a hidden button cannot take focus.
			nextTick(focusPrimary);
		});
	});
};

// Immediate, so a tour that is already running when this mounts (a step on the next page) opens
// its step here.
watch(
	() => [props.active, props.stepIndex],
	([isActive]) => {
		if (isActive) {
			nextTick(enterStep);
		} else {
			entry += 1;
			ready.value = false;
		}
	},
	{ immediate: true },
);

onBeforeUnmount(() => {
	entry += 1;
});

// Never stops or prevents the click: the page's own handler for the target must still run.
useEventListener(targetEl, 'click', () => {
	if (props.active && isInteractive.value) {
		emit('target-click');
	}
});

useEventListener(document, 'keydown', (event) => {
	if (props.active && event.key === 'Escape') {
		emit('escape');
	}
});

// Capture phase so we also catch scrolls of nested overflow-y-scroll containers (scroll events
// don't bubble).
useEventListener(window, 'scroll', recompute, { capture: true, passive: true });
useEventListener(window, 'resize', recompute);
useResizeObserver(resolveTarget, recompute);
// A live update can move the target without a scroll or a resize, such as a queue row sorting.
useMutationObserver(() => (props.active ? document.body : null), recompute, { childList: true, subtree: true });
</script>

<style scoped>
.tutorial-tour-spotlight {
	box-shadow:
		rgb(255 255 255) 0 0 0 0,
		rgb(33 33 33 / 50%) 0 0 0 9999px;
	transition: all 0.2s ease;
}

.tutorial-tour-spotlight--below {
	box-shadow: rgb(17 24 39 / 80%) 0 0 0 9999px;
}

.tutorial-tour-tooltip {
	transition: opacity 0.15s ease;
}

.tutorial-tour-fade-enter-active,
.tutorial-tour-fade-leave-active {
	transition: opacity 0.2s ease;
}

.tutorial-tour-fade-enter-from,
.tutorial-tour-fade-leave-to {
	opacity: 0;
}

/* Vue times a transition by its duration, so with no transition the fade completes at once too. */
@media (prefers-reduced-motion: reduce) {
	.tutorial-tour-spotlight,
	.tutorial-tour-tooltip,
	.tutorial-tour-fade-enter-active,
	.tutorial-tour-fade-leave-active {
		transition: none;
	}
}
</style>
